import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { Box, Paper, Typography, Grid, Tooltip } from '@mui/material';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';

import { NimbuxHotelAmenity } from '@bandido/shared/interfaces';

import { useGetHotelAmenities, useGetHotelNimbuxHotelById } from '@frontend/features/hotel-content-manager/queries';
import { usePatchHotel } from '@frontend/features/hotel-content-manager/mutations';
import { QueryKeys } from '@frontend/features/common/types';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { DynamicMUIIcon } from '@frontend/features/common/utils/iconResolver';
import { LoadingButton } from '@mui/lab';
import { LoadingSkeleton } from '@frontend/features/hotel-content-manager/components/Jobs/LoadingSkeleton';


export const HotelContentAmenities: React.FC = () => {
  const { hotelId } = useParams();
  const queryClient = useQueryClient();
  const { addToast } = useSnackBar();

  const { data: amenitiesList } = useGetHotelAmenities({
    currentPage: 1,
    pageSize: 100,
    searchText: '',
    sort: 'id',
  });
  const { isLoading: isLoadingNimbuxHotel, data: nimbuxHotel } = useGetHotelNimbuxHotelById(Number(hotelId));

  const { mutate: updateHotelAmenities, isLoading: isUpdatingHotelAmenities } = usePatchHotel(
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.getHotelNimbuxHotelById, Number(hotelId)]);
        addToast({
          color: 'success',
          message: t('hotelContent.view.amenities.messages.updateSuccess'),
        });
      },
      onError: () => {
        addToast({
          color: 'error',
          message: t('hotelContent.view.amenities.messages.updateError'),
        });
      },
    }
  );

  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language.split('-')[0] as 'en' | 'es' | 'pt';
  const [selectedAmenities, setSelectedAmenities] = useState<NimbuxHotelAmenity[]>([]);


  const handleAmenityToggle = (id: number) => {
    const selectedAmenity = amenitiesList?.items?.find(amenity => amenity.id === id);
    if (!selectedAmenity) return;
    setSelectedAmenities(prev => {
      if (prev.find(amenity => amenity.id === selectedAmenity.id)) {
        return prev.filter(amenity => amenity.id !== selectedAmenity.id);
      } else {
        return [...prev, selectedAmenity];
      }
    });
  };

  const getAmentiyById = (id: number) => amenitiesList?.items?.find(amenity => amenity.id === id);

  const handleUpdateHotelAmenities = () => {
    updateHotelAmenities({
      id: Number(hotelId),
      amenities: selectedAmenities,
    });
  };

  useEffect(() => {
    setSelectedAmenities(nimbuxHotel?.amenities || []);
  }, [nimbuxHotel]);

  return (
    <Paper elevation={0} sx={{ mb: 8, maxWidth:1082 }}>
      { isLoadingNimbuxHotel && <LoadingSkeleton /> }
      { !isLoadingNimbuxHotel && nimbuxHotel && (
        <Box display='flex' flexDirection='column' gap={5} p={2} borderRadius={2} >
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            
            <Typography variant="h6">{t('hotelContent.view.amenities.title')}</Typography>
            <LoadingButton
              variant="contained"
              color="primary"
              type="submit"
              size='small'
              onClick={handleUpdateHotelAmenities}
              loading={isUpdatingHotelAmenities}
            >
              {t('common.form.save')}
            </LoadingButton>
          </Box>

          {selectedAmenities.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 3,
                backgroundColor: 'grey.50',
                borderRadius: 2,
              }}
            >
              <Typography variant="body1" my={2}>
                {t('hotelContent.view.amenities.selectedAmenities', { count: selectedAmenities.length })}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedAmenities.map(({ id }) => (
                  <Tooltip key={id} title={getAmentiyById(id)?.texts[currentLanguage] || ''}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'default',
                      }}
                    >
                      <DynamicMUIIcon
                        iconName={getAmentiyById(id)?.icon || ''}
                        fontSize="small"
                        color="inherit"
                      />
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}

          <Grid container spacing={2}>
            {amenitiesList?.items?.map(({ id, icon, texts }) => {
              const isSelected = selectedAmenities.some(amenity => amenity.id === id);

              return (
                <Grid item xs={12} sm={6} md={4} key={id}>
                  {icon}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      border: 1,
                      borderColor: isSelected ? 'primary.main' : 'grey.300',
                      borderRadius: 2,
                      backgroundColor: isSelected ? 'primary.50' : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: isSelected ? 'primary.main' : 'grey.400',
                        backgroundColor: isSelected ? 'primary.50' : 'grey.50',
                      },
                    }}
                    onClick={() => handleAmenityToggle(id)}
                  >
                    <Box
                      sx={{
                        mr: 1,
                        display: 'flex',
                        alignItems: 'center',
                        color: isSelected ? 'primary.main' : 'grey.400',
                        cursor: 'pointer',
                      }}
                    >
                      {isSelected ? (
                        <CheckCircle fontSize="small" />
                      ) : (
                        <RadioButtonUnchecked fontSize="small" />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          color: 'grey.600',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <DynamicMUIIcon
                          iconName={icon}
                          fontSize="small"
                          color="inherit"
                        />
                      </Box>
                      <Typography variant="body2" fontWeight="500">
                        {texts[currentLanguage]}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </Paper>
  );
};
